import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  MOCK_STORY_FIT_VARIANTS,
  STORY_FIT_LEGEND,
  type StoryFitAmazonData,
  type StoryFitCompetencyRow,
  type StoryFitGoogleAttribute,
  type StoryFitNetflixCard,
  type StoryFitSignal,
  type StoryFitVariant,
} from '../../_mock-data'
import { ScoreBar } from '../../shared/charts'
import { CompanyIcon } from '../../shared/pills'
import { SectionCard } from '../../shared/section-card'

/**
 * Story Fit — company-aware variants (spec §8.7). One component switching on
 * `variant`, sharing the glyph vocabulary `●●●`/`●●○`/`●○○`/`○○○` + a
 * monospace S/T/A/R tag across all five layouts. The behavioral page's own
 * simpler ✓-pill variant is superseded by the General layout here
 * (spec §13).
 *
 * The `variant` prop seeds the demo picker's initial value only — the
 * `{/* demo-only *\/}` `<select>` below owns which variant actually renders
 * from then on, standing in for "the company comes from the assignment"
 * until that's wired up. No composer/comment plumbing here: the mock
 * behavioral seed has no comments targeting the `story` section, and none
 * of the five layouts expose a per-row comment affordance.
 */

const SIGNAL_META: Record<StoryFitSignal, { glyph: string; color: string }> = {
  strong: { glyph: '●●●', color: 'var(--cd-green)' },
  partial: { glyph: '●●○', color: 'var(--cd-amber)' },
  weak: { glyph: '●○○', color: 'var(--cd-red)' },
  none: { glyph: '○○○', color: 'var(--cd-text-ghost)' },
}

const VARIANT_LABEL: Record<StoryFitVariant, string> = {
  general: 'General',
  amazon: 'Amazon',
  meta: 'Meta',
  google: 'Google',
  netflix: 'Netflix',
}

function SignalTag(props: { tag?: 'S' | 'T' | 'A' | 'R' }): ReactNode {
  const { tag } = props
  if (!tag) return null
  return (
    <span
      className="shrink-0 rounded-[5px] border border-(--cd-border-2) bg-(--cd-bg) px-1.5 py-px font-mono text-[10px] font-bold text-(--cd-text-muted)"
      style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
    >
      {tag}
    </span>
  )
}

function SignalGlyph(props: { strength: StoryFitSignal }): ReactNode {
  const meta = SIGNAL_META[props.strength]
  return (
    <span
      className="shrink-0 text-[9px] tracking-[2px]"
      style={{ color: meta.color }}
    >
      {meta.glyph}
    </span>
  )
}

function Legend(): ReactNode {
  return (
    <div className="mt-2.5 text-[11px] leading-relaxed text-(--cd-text-faint)">
      {STORY_FIT_LEGEND}
    </div>
  )
}

function HeaderChip(props: { children: ReactNode }): ReactNode {
  return (
    <span className="shrink-0 rounded-full border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-0.5 text-[11px] text-(--cd-text-2)">
      {props.children}
    </span>
  )
}

function CompanyChip(props: {
  company: string
  children: ReactNode
}): ReactNode {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-0.5 text-[11px] text-(--cd-text-2)">
      <CompanyIcon company={props.company} size={14} />
      {props.children}
    </span>
  )
}

function GeneralBody(props: { rows: StoryFitCompetencyRow[] }): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      {props.rows.map((row) => (
        <div
          key={row.label}
          className={
            row.notSignaled
              ? 'flex items-center gap-2.5 rounded-lg border border-dashed border-(--cd-border-3) bg-(--cd-inset) px-2.5 py-2 opacity-75'
              : 'flex items-center gap-2.5 rounded-lg border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-2'
          }
        >
          <span
            className="flex-1 text-[12.5px] font-semibold"
            style={{
              color: row.notSignaled ? 'var(--cd-text-muted)' : undefined,
            }}
          >
            {row.label}
          </span>
          <SignalTag tag={row.tag} />
          {row.notSignaled && (
            <span className="text-[10.5px] text-(--cd-text-faint)">
              not signaled
            </span>
          )}
          <SignalGlyph strength={row.strength} />
        </div>
      ))}
    </div>
  )
}

function AmazonBody(props: { data: StoryFitAmazonData }): ReactNode {
  const { data } = props
  return (
    <>
      <div className="mb-1.5 text-[10.5px] font-bold tracking-wide text-(--cd-green)">
        SIGNALED · {data.signaledCount} OF {data.totalCount}
      </div>
      <div className="mb-3 flex flex-col gap-1.5">
        {data.signaled.map((row) => (
          <div
            key={row.label}
            className="rounded-lg border px-2.5 py-2"
            style={{
              borderColor:
                row.strength === 'weak'
                  ? 'color-mix(in oklab, var(--cd-red) 30%, transparent)'
                  : row.strength === 'strong'
                    ? 'color-mix(in oklab, var(--cd-green) 25%, transparent)'
                    : 'var(--cd-border-2)',
              background: 'var(--cd-inset)',
            }}
          >
            <div className="flex items-center gap-2.5">
              <span className="flex-1 text-[12.5px] font-semibold">
                {row.label}
              </span>
              <SignalTag tag={row.tag} />
              <SignalGlyph strength={row.strength} />
            </div>
            <div
              className="mt-1 text-[11px] leading-relaxed"
              style={{
                color:
                  row.strength === 'weak'
                    ? 'var(--cd-red-soft)'
                    : 'var(--cd-text-muted)',
              }}
            >
              {row.evidence}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-1.5 text-[10.5px] font-bold tracking-wide text-(--cd-text-faint)">
        NOT SIGNALED · {data.notSignaled.length}
      </div>
      <div className="mb-2.5 flex flex-wrap gap-1.5">
        {data.notSignaled.map((label) => (
          <span
            key={label}
            className="rounded-full border border-(--cd-border) bg-(--cd-bg) px-2.5 py-0.5 text-[10.5px] text-(--cd-text-faint)"
          >
            {label}
          </span>
        ))}
      </div>
      <div className="text-[11px] leading-relaxed text-(--cd-text-faint)">
        {data.coachTip}
      </div>
    </>
  )
}

function MetaBody(props: { rows: StoryFitCompetencyRow[] }): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      {props.rows.map((row) => (
        <div
          key={row.label}
          className={
            row.notSignaled
              ? 'flex items-center gap-2.5 rounded-lg border border-dashed border-(--cd-border-3) bg-(--cd-inset) px-2.5 py-2 opacity-75'
              : 'flex items-center gap-2.5 rounded-lg border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-2'
          }
        >
          <span className="flex-1">
            <span
              className="block text-[12.5px] font-semibold"
              style={{
                color: row.notSignaled ? 'var(--cd-text-muted)' : undefined,
              }}
            >
              {row.label}
            </span>
            <span className="mt-0.5 block text-[11px] text-(--cd-text-faint)">
              {row.evidence}
            </span>
          </span>
          <SignalTag tag={row.tag} />
          <SignalGlyph strength={row.strength} />
        </div>
      ))}
    </div>
  )
}

function GoogleBody(props: { rows: StoryFitGoogleAttribute[] }): ReactNode {
  return (
    <div className="flex flex-col gap-2.5">
      {props.rows.map((row) => {
        const color =
          row.level === 'Strong' ? 'var(--cd-green)' : 'var(--cd-amber)'
        return (
          <div key={row.label}>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-[12.5px] font-semibold">{row.label}</span>
              <span className="text-[10.5px] font-bold" style={{ color }}>
                {row.level}
              </span>
            </div>
            <ScoreBar percent={row.pct} color={color} />
            <div className="mt-1 text-[11px] text-(--cd-text-faint)">
              {row.evidence}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function NetflixBody(props: { cards: StoryFitNetflixCard[] }): ReactNode {
  return (
    <div className="flex flex-col gap-2">
      {props.cards.map((card) => {
        const meta = SIGNAL_META[card.strength]
        const dim = card.strength === 'none'
        return (
          <div
            key={card.label}
            className="rounded-r-lg border-l-[3px] bg-(--cd-inset) py-2.5 pr-3 pl-3"
            style={{ borderLeftColor: meta.color, opacity: dim ? 0.8 : 1 }}
          >
            <div className="mb-0.5 flex items-center gap-2">
              <span
                className="text-[12.5px] font-semibold"
                style={{ color: dim ? 'var(--cd-text-muted)' : undefined }}
              >
                {card.label}
              </span>
              <span className="ml-auto">
                <SignalGlyph strength={card.strength} />
              </span>
            </div>
            <div className="text-[11px] leading-relaxed text-(--cd-text-faint)">
              {card.evidence}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function StoryFit(props: { variant: StoryFitVariant }): ReactNode {
  // Seeding state from props is safe here only because ReviewQueue renders
  // this subtree under `<ExpandedCard key={submission.id}>` — switching
  // submissions remounts StoryFit, re-seeding demoVariant from the new
  // submission's targetCompany. Without that keyed remount a stale demo
  // selection would leak across submissions.
  const [demoVariant, setDemoVariant] = useState<StoryFitVariant>(props.variant)
  const data = MOCK_STORY_FIT_VARIANTS[demoVariant]

  return (
    <SectionCard title="Story fit">
      <div className="mb-1 flex items-center justify-between gap-2">
        {demoVariant === 'general' ? (
          <HeaderChip>{data.headerChip}</HeaderChip>
        ) : (
          <CompanyChip company={demoVariant}>{data.headerChip}</CompanyChip>
        )}
      </div>
      <div className="mb-3 text-[11.5px] text-(--cd-text-faint)">
        {data.hint}
      </div>

      {demoVariant === 'general' && (
        <GeneralBody rows={data.competencies ?? []} />
      )}
      {demoVariant === 'amazon' && data.amazon && (
        <AmazonBody data={data.amazon} />
      )}
      {demoVariant === 'meta' && <MetaBody rows={data.metaRows ?? []} />}
      {demoVariant === 'google' && (
        <GoogleBody rows={data.googleAttributes ?? []} />
      )}
      {demoVariant === 'netflix' && (
        <NetflixBody cards={data.netflixCards ?? []} />
      )}

      <Legend />

      {/* demo-only: remove when company comes from the assignment */}
      <div className="mt-3 flex items-center gap-2 border-t border-(--cd-border) pt-2.5">
        <label
          htmlFor="story-fit-demo-variant"
          className="text-[10.5px] text-(--cd-text-faint)"
        >
          Demo — variant
        </label>
        <select
          id="story-fit-demo-variant"
          aria-label="Story Fit variant (demo)"
          value={demoVariant}
          onChange={(e) => setDemoVariant(e.target.value as StoryFitVariant)}
          className="rounded-md border border-(--cd-border-2) bg-(--cd-inset) px-2 py-1 text-[11px] text-(--cd-text-2)"
        >
          {(Object.keys(VARIANT_LABEL) as StoryFitVariant[]).map((v) => (
            <option key={v} value={v}>
              {VARIANT_LABEL[v]}
            </option>
          ))}
        </select>
      </div>
    </SectionCard>
  )
}
